import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import IntegrationCapabilities from "@/components/IntegrationCapabilities";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Mail, KeyRound, RefreshCw, Loader2, CheckCircle2, AlertOctagon,
  Users, Building2, TrendingUp, ShieldAlert, Trash2, Copy, Plug, Eye, EyeOff,
  UserPlus,
} from "lucide-react";
import { CreateSubuserDialog } from "@/components/sendgrid/SendGridDialogs";

const TABS = [
  { key: "subusers", label: "Subusers" },
  { key: "stats", label: "Stats (30 d)" },
  { key: "suppressions", label: "Suppressions" },
];

export default function SendGridMasterPage() {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [clients, setClients] = useState([]);
  const [tab, setTab] = useState("subusers");
  const [keyDraft, setKeyDraft] = useState("");
  const [syncing, setSyncing] = useState(false);
  const [showCreate, setShowCreate] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, c] = await Promise.all([
        api.get("/sendgrid/master"),
        api.get("/clients"),
      ]);
      setState(s.data);
      setClients(c.data || []);
    } catch (e) {
      toast.error("Failed to load SendGrid state");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveKey = async () => {
    if (!keyDraft.trim()) return;
    try {
      await api.put("/sendgrid/master/api-key", { api_key: keyDraft.trim() });
      toast.success("API key saved");
      setKeyDraft("");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  const clearKey = async () => {
    if (!window.confirm("Clear the SendGrid master API key?")) return;
    try {
      await api.delete("/sendgrid/master/api-key");
      toast.success("Cleared"); await load();
    } catch (e) {
      toast.error("Failed");
    }
  };

  const sync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/sendgrid/master/sync");
      toast.success(`${data.subusers || 0} subusers · ${data.stats_rows || 0} stat rows · ${data.suppressions || 0} suppressions`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sync failed");
    } finally { setSyncing(false); }
  };

  const setIceberg = async (sub) => {
    try {
      await api.put("/sendgrid/master/iceberg-subuser",
        { subuser_username: sub || "" });
      toast.success(sub ? `Iceberg master subuser set to ${sub}` : "Iceberg subuser cleared");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const assignToClient = async (sub, clientId) => {
    try {
      if (clientId === "__unassign__") {
        await api.put(`/clients/${sub}/sendgrid/subuser`, { subuser_username: null });
        return;
      }
      await api.put(`/clients/${clientId}/sendgrid/subuser`,
        { subuser_username: sub });
      toast.success("Linked");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const unassign = async (clientId) => {
    try {
      await api.put(`/clients/${clientId}/sendgrid/subuser`, { subuser_username: null });
      toast.success("Unlinked");
      await load();
    } catch (e) { toast.error("Failed"); }
  };

  if (loading) {
    return (
      <div className="p-8 text-sm text-zinc-500 flex items-center gap-2"
        data-testid="sg-loading">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading SendGrid…
      </div>
    );
  }
  if (!state) return null;

  return (
    <div className="space-y-6" data-testid="sg-master-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="label-overline mb-2">Communications</div>
          <h1 className="font-display font-extrabold text-4xl tracking-tight">SendGrid</h1>
          <p className="text-sm text-zinc-500 mt-2 max-w-2xl">
            One master API key for the whole agency. Assign a SendGrid subuser to each
            client so their outbound mail bills + reports under their own account.
            For Iceberg admin mail (welcomes, password resets, setup), set an Iceberg
            subuser below — used as the fallback for clients with no subuser yet.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="rounded-sm">
            {state.configured ? (
              <><CheckCircle2 className="h-3 w-3 mr-1 text-emerald-600" /> Master key configured</>
            ) : (
              <><AlertOctagon className="h-3 w-3 mr-1 text-amber-600" /> No master key</>
            )}
          </Badge>
          <Button variant="outline" size="sm" className="rounded-sm"
            onClick={sync} disabled={!state.configured || syncing}
            data-testid="sg-sync">
            {syncing ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />}
            Sync now
          </Button>
        </div>
      </div>

      {/* Master API key + Iceberg subuser */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <Card className="rounded-sm border-zinc-200">
          <CardContent className="p-5">
            <div className="label-overline mb-3 flex items-center gap-1">
              <KeyRound className="h-3 w-3" /> Master API key
            </div>
            <div className="flex items-end gap-2">
              <div className="flex-1">
                <Label className="label-overline mb-1.5">Paste your SendGrid master key (starts with <code>SG.</code>)</Label>
                <Input value={keyDraft} type="password"
                  onChange={(e) => setKeyDraft(e.target.value)}
                  placeholder="SG.xxxxxxxxxxxxxxxxxxxxxx"
                  className="rounded-sm font-mono"
                  data-testid="sg-master-key-input" />
              </div>
              <Button onClick={saveKey} disabled={!keyDraft.trim()}
                className="rounded-sm bg-[#002FA7] text-white hover:bg-[#00227A]"
                data-testid="sg-save-master-key">
                Save
              </Button>
              {state.configured && (
                <Button variant="outline" onClick={clearKey}
                  className="rounded-sm border-red-200 text-red-700 hover:bg-red-50"
                  data-testid="sg-clear-master-key">
                  <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Clear
                </Button>
              )}
            </div>
            {state.master_profile?.first_name && (
              <div className="mt-3 text-xs text-zinc-500">
                <strong className="text-zinc-700">Account:</strong> {state.master_profile.first_name} {state.master_profile.last_name}
                {state.master_profile.country && <> · {state.master_profile.city}, {state.master_profile.country}</>}
              </div>
            )}
            {state.last_sync_at && (
              <div className="text-[11px] font-mono text-zinc-400 mt-1">
                Last synced: {new Date(state.last_sync_at).toLocaleString()}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="rounded-sm border-zinc-200">
          <CardContent className="p-5">
            <div className="label-overline mb-3 flex items-center gap-1">
              <Building2 className="h-3 w-3" /> Iceberg master subuser
            </div>
            <p className="text-xs text-zinc-500 mb-3">
              All Iceberg admin email (welcomes, password resets, setup) flows
              through this subuser. Also used as the fallback for clients without
              their own subuser assigned.
            </p>
            <div className="flex items-center gap-2">
              <Select value={state.iceberg_subuser || "__none__"}
                onValueChange={(v) => setIceberg(v === "__none__" ? "" : v)}>
                <SelectTrigger className="rounded-sm flex-1" data-testid="sg-iceberg-subuser-select">
                  <SelectValue placeholder="Pick a subuser…" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">(none — use env fallback)</SelectItem>
                  {state.subusers.map((s) => (
                    <SelectItem key={s.username} value={s.username}>
                      {s.username} — {s.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {state.subusers.length === 0 && (
              <div className="mt-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-sm p-2">
                No subusers discovered yet. Click <strong>Sync now</strong> above after saving the master key.
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Global stats */}
      {Object.keys(state.global_stats || {}).length > 0 && (
        <Card className="rounded-sm border-zinc-200">
          <CardContent className="p-5">
            <div className="label-overline mb-3 flex items-center gap-1">
              <TrendingUp className="h-3 w-3" /> Master account · last 30 days
            </div>
            <StatGrid stats={state.global_stats} />
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <div className="flex items-center justify-between gap-2 border-b border-zinc-200">
        <div className="flex items-center gap-1">
          {TABS.map((t) => (
            <button key={t.key} type="button"
              className={`px-4 py-2 text-sm border-b-2 -mb-px transition-colors ${
                tab === t.key
                  ? "border-[#002FA7] text-[#002FA7] font-semibold"
                  : "border-transparent text-zinc-500 hover:text-zinc-800"
              }`}
              onClick={() => setTab(t.key)}
              data-testid={`sg-tab-${t.key}`}>
              {t.label}
            </button>
          ))}
        </div>
        {tab === "subusers" && state?.configured && (
          <Button size="sm" onClick={() => setShowCreate(true)}
            className="rounded-sm bg-[#002FA7] hover:bg-[#001F7A] mb-1"
            data-testid="sg-open-create-subuser">
            <UserPlus className="h-3.5 w-3.5 mr-1.5" />
            Create subuser
          </Button>
        )}
      </div>

      <CreateSubuserDialog open={showCreate} onOpenChange={setShowCreate}
        clients={clients} onCreated={() => { setShowCreate(false); load(); }} />

      {tab === "subusers" && (
        <SubusersTab subusers={state.subusers} clients={clients}
          onAssign={assignToClient} onUnassign={unassign} />
      )}
      {tab === "stats" && (
        <StatsTab subusers={state.subusers} />
      )}
      {tab === "suppressions" && (
        <SuppressionsTab configured={state.configured} />
      )}

      <IntegrationCapabilities integrationKey="sendgrid" accent="#1A82E2" />
    </div>
  );
}

function StatGrid({ stats }) {
  const cells = [
    ["Requests", stats.requests],
    ["Delivered", stats.delivered],
    ["Opens", stats.unique_opens, `${stats.opens || 0} total`],
    ["Clicks", stats.unique_clicks, `${stats.clicks || 0} total`],
    ["Bounces", stats.bounces],
    ["Blocks", stats.blocks],
    ["Spam", stats.spam_reports],
    ["Unsub", stats.unsubscribes],
  ];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2 text-xs">
      {cells.map(([label, value, sub]) => (
        <div key={label} className="rounded-sm border border-zinc-200 bg-zinc-50/40 px-2 py-2">
          <div className="label-overline">{label}</div>
          <div className="font-display font-bold text-lg mt-0.5">
            {Number(value || 0).toLocaleString()}
          </div>
          {sub && <div className="text-[10px] font-mono text-zinc-500">{sub}</div>}
        </div>
      ))}
    </div>
  );
}

function SubusersTab({ subusers, clients, onAssign, onUnassign }) {
  const [credsFor, setCredsFor] = useState(null);
  if (subusers.length === 0) {
    return (
      <Card className="rounded-sm border-dashed border-zinc-300">
        <CardContent className="p-8 text-center text-sm text-zinc-500">
          <Users className="h-8 w-8 mx-auto mb-2 text-zinc-300" />
          No subusers yet. Save the master API key above and click <strong>Sync now</strong>.
        </CardContent>
      </Card>
    );
  }
  return (
    <>
    <Card className="rounded-sm border-zinc-200 overflow-hidden">
      <CardContent className="p-0">
        <div className="divide-y divide-zinc-100">
          {subusers.map((s) => (
            <div key={s.username}
              className="px-5 py-3 grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-3 items-center"
              data-testid={`sg-subuser-row-${s.username}`}>
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-display font-bold text-sm">{s.username}</span>
                  {s.disabled && (
                    <Badge variant="outline" className="rounded-sm text-[10px] border-amber-300 text-amber-800 bg-amber-50">disabled</Badge>
                  )}
                  {(s.assigned_clients || []).map((c) => (
                    <Badge key={c.client_id}
                      className="rounded-sm bg-emerald-100 text-emerald-800 border-emerald-200 text-[10px]">
                      → {c.company_name}
                      <button type="button" onClick={() => onUnassign(c.client_id)}
                        className="ml-1 opacity-60 hover:opacity-100"
                        data-testid={`sg-unassign-${c.client_id}`}>×</button>
                    </Badge>
                  ))}
                </div>
                <div className="text-[11px] font-mono text-zinc-500 mt-0.5">{s.email}</div>
              </div>
              <div className="flex items-center gap-2">
                <Button type="button" variant="outline" size="sm" className="rounded-sm"
                  onClick={() => setCredsFor(s.username)}
                  data-testid={`sg-show-creds-${s.username}`}>
                  <Plug className="h-3.5 w-3.5 mr-1" /> Connection details
                </Button>
                <Select onValueChange={(v) => onAssign(s.username, v)}>
                  <SelectTrigger className="rounded-sm w-[260px]"
                    data-testid={`sg-assign-${s.username}`}>
                    <SelectValue placeholder="Link to client…" />
                  </SelectTrigger>
                  <SelectContent>
                    {(clients || []).map((c) => (
                      <SelectItem key={c.client_id} value={c.client_id}>
                        {c.company_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
    <SubuserCredentialsDialog
      username={credsFor}
      open={!!credsFor}
      onClose={() => setCredsFor(null)}
    />
    </>
  );
}



// Render a created_at ISO string in BOTH UTC and Sydney time. SendGrid's
// /api_keys list endpoint doesn't return a creation timestamp, so this
// only renders for keys minted *through this UI* (we track those in
// `sendgrid_api_keys`); pre-existing keys show "—".
function formatKeyTimestamp(iso) {
  if (!iso) return "Created: — (legacy / pre-tracking)";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "Created: —";
  // UTC: YYYY-MM-DD HH:MM UTC
  const pad = (n) => String(n).padStart(2, "0");
  const utc = `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())} ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())} UTC`;
  // Sydney: localised via Intl, en-AU, day-month-year + 24h time
  let syd = "";
  try {
    syd = d.toLocaleString("en-AU", {
      timeZone: "Australia/Sydney",
      year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit", hour12: false,
    }).replace(",", "");
  } catch (e) {
    syd = "";
  }
  return syd ? `${utc} · ${syd} Sydney` : utc;
}


function CopyField({ label, value, testid, mono = true }) {
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value || "");
      toast.success(`Copied ${label.toLowerCase()}`);
    } catch (e) {
      toast.error("Copy failed");
    }
  };
  return (
    <div className="space-y-1">
      <div className="label-overline">{label}</div>
      <div className="flex items-stretch gap-1">
        <div className={`flex-1 px-3 py-2 rounded-sm border border-zinc-200 bg-zinc-50 text-[12px] ${mono ? "font-mono" : ""} text-zinc-800 overflow-x-auto whitespace-nowrap`}
          data-testid={`${testid}-value`}>
          {value || <span className="text-zinc-400">—</span>}
        </div>
        <Button type="button" variant="outline" size="sm" className="rounded-sm shrink-0"
          onClick={copy} disabled={!value} data-testid={`${testid}-copy`}>
          <Copy className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}


function SubuserCredentialsDialog({ username, open, onClose }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [generated, setGenerated] = useState(null);  // {api_key, api_key_id, name}
  const [revealKey, setRevealKey] = useState(false);
  const [keyName, setKeyName] = useState("");
  const [deletingId, setDeletingId] = useState(null);

  const refetch = useCallback(async () => {
    if (!username) return;
    try {
      const { data: d } = await api.get(`/sendgrid/master/subusers/${encodeURIComponent(username)}/credentials`);
      setData(d);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to load credentials");
    }
  }, [username]);

  useEffect(() => {
    if (!open || !username) {
      setData(null); setGenerated(null); setError(null);
      setRevealKey(false); setKeyName(""); setDeletingId(null);
      return;
    }
    let active = true;
    (async () => {
      setLoading(true); setError(null);
      try {
        const { data: d } = await api.get(`/sendgrid/master/subusers/${encodeURIComponent(username)}/credentials`);
        if (active) setData(d);
      } catch (e) {
        if (active) setError(e.response?.data?.detail || "Failed to load credentials");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [open, username]);

  const generate = async () => {
    if (!username) return;
    const name = keyName.trim();
    if (!name) {
      toast.error("Give the key a name first (e.g. 'PHAFL Portal').");
      return;
    }
    if (!window.confirm(`Generate a new Full Access API key named "${name}" for this subuser? Anyone with this key can send mail on the subuser's behalf — store it securely.`)) return;
    setGenerating(true);
    try {
      const { data: d } = await api.post(`/sendgrid/master/subusers/${encodeURIComponent(username)}/api-keys`, { name });
      setGenerated(d);
      setRevealKey(true);
      setKeyName("");
      toast.success("New API key generated — copy it now");
      await refetch();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Generation failed");
    } finally { setGenerating(false); }
  };

  const deleteKey = async (k) => {
    if (!username || !k?.api_key_id) return;
    const label = k.name || k.api_key_id;
    const confirmed = window.confirm(
      `⚠️ Permanently delete the API key "${label}"?\n\n` +
      `This CANNOT be undone. Any external platform still using this key ` +
      `will immediately stop being able to send via SendGrid (401 errors).\n\n` +
      `Are you sure you want to proceed?`
    );
    if (!confirmed) return;
    setDeletingId(k.api_key_id);
    try {
      await api.delete(`/sendgrid/master/subusers/${encodeURIComponent(username)}/api-keys/${encodeURIComponent(k.api_key_id)}`);
      toast.success(`Deleted "${label}"`);
      // If the freshly-generated key was just revoked, clear it from the panel.
      if (generated?.api_key_id === k.api_key_id) {
        setGenerated(null);
        setRevealKey(false);
      }
      await refetch();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    } finally { setDeletingId(null); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl rounded-sm" data-testid="sg-creds-dialog">
        <DialogHeader>
          <DialogTitle className="font-display font-extrabold flex items-center gap-2">
            <Plug className="h-5 w-5" /> SendGrid connection details
          </DialogTitle>
          <DialogDescription>
            SMTP relay settings + API key for <span className="font-mono">{username}</span>. Use these to plug this subaccount into external systems (e.g. PHAFL).
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-10 text-center text-sm text-zinc-500 flex items-center justify-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : error ? (
          <div className="py-6 text-center text-sm text-rose-600 flex flex-col items-center gap-2">
            <AlertOctagon className="h-5 w-5" /> {error}
          </div>
        ) : data ? (
          <div className="space-y-4">
            {/* SMTP block */}
            <div className="space-y-2">
              <div className="text-xs uppercase tracking-wider font-bold text-zinc-700">SMTP relay</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <CopyField label="Host" value={data.smtp?.host} testid="sg-creds-host" />
                <CopyField label="Port (STARTTLS)" value={String(data.smtp?.port_starttls || "")} testid="sg-creds-port" />
                <CopyField label="Username" value={data.smtp?.username} testid="sg-creds-username" />
                <CopyField label="From email (verified sender)" value={data.from_email} testid="sg-creds-from" />
              </div>
              <p className="text-[11px] text-zinc-500 mt-1">
                Password = the API key value below. SendGrid also supports port {data.smtp?.port_ssl} (SSL) and {data.smtp?.port_plain} (plain).
              </p>
            </div>

            {/* Existing keys */}
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-3">
                <div className="text-xs uppercase tracking-wider font-bold text-zinc-700">API keys on this subuser</div>
                {data.api_keys?.length > 0 && (
                  <Badge variant="outline" className="rounded-sm text-[10px]">
                    {data.api_keys.length} existing
                  </Badge>
                )}
              </div>
              {data.api_keys_error && (
                <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-sm px-3 py-2">
                  Couldn&apos;t list existing keys: {data.api_keys_error}
                </div>
              )}
              {data.api_keys?.length === 0 ? (
                <div className="text-[12px] text-zinc-500 italic">No API keys yet for this subuser.</div>
              ) : (
                <div className="rounded-sm border border-zinc-200 divide-y divide-zinc-100 max-h-56 overflow-y-auto">
                  {(data.api_keys || []).map((k) => (
                    <div key={k.api_key_id} className="px-3 py-2 text-[12px] flex items-center justify-between gap-2"
                      data-testid={`sg-creds-existing-${k.api_key_id}`}>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-zinc-700 truncate">{k.name || "(unnamed)"}</span>
                          <span className="font-mono text-[10px] text-zinc-400 shrink-0">{k.api_key_id}</span>
                        </div>
                        <div className="text-[10px] text-zinc-500 mt-0.5 font-mono"
                          data-testid={`sg-creds-existing-${k.api_key_id}-when`}>
                          {formatKeyTimestamp(k.created_at)}
                        </div>
                      </div>
                      <Button type="button" variant="ghost" size="icon"
                        className="h-6 w-6 shrink-0 text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                        onClick={() => deleteKey(k)}
                        disabled={deletingId === k.api_key_id}
                        title="Delete this API key"
                        data-testid={`sg-creds-delete-${k.api_key_id}`}>
                        {deletingId === k.api_key_id ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <Trash2 className="h-3.5 w-3.5" />
                        )}
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Generated key (one-time) */}
            {generated && (
              <div className="space-y-2 border border-emerald-200 bg-emerald-50 rounded-sm p-3">
                <div className="text-xs uppercase tracking-wider font-bold text-emerald-800 flex items-center gap-1">
                  <CheckCircle2 className="h-3.5 w-3.5" /> New API key (shown once)
                </div>
                <div className="space-y-2">
                  <div className="space-y-1">
                    <div className="label-overline">API key</div>
                    <div className="flex items-stretch gap-1">
                      <div className="flex-1 px-3 py-2 rounded-sm border border-zinc-200 bg-white font-mono text-[12px] text-zinc-800 overflow-x-auto whitespace-nowrap"
                        data-testid="sg-creds-new-key-value">
                        {revealKey ? generated.api_key : "•".repeat(Math.min(generated.api_key?.length || 40, 60))}
                      </div>
                      <Button type="button" variant="outline" size="sm" className="rounded-sm shrink-0"
                        onClick={() => setRevealKey((r) => !r)}
                        data-testid="sg-creds-new-key-toggle">
                        {revealKey ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                      </Button>
                      <Button type="button" variant="outline" size="sm" className="rounded-sm shrink-0"
                        onClick={async () => {
                          try {
                            await navigator.clipboard.writeText(generated.api_key);
                            toast.success("API key copied");
                          } catch (e) { toast.error("Copy failed"); }
                        }}
                        data-testid="sg-creds-new-key-copy">
                        <Copy className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                  <div className="text-[11px] text-emerald-800">
                    Save it now — SendGrid won&apos;t show this value again. Name: <span className="font-mono">{generated.name}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Generate new key */}
            <div className="border border-dashed border-zinc-300 rounded-sm p-3 space-y-2">
              <div className="text-[12px] text-zinc-600">
                Need a fresh key to paste into an external platform? Name it (e.g. <span className="font-mono">PHAFL Portal</span>, <span className="font-mono">Trent Test</span>) then generate — the key value will be visible <strong>only once</strong>.
              </div>
              <div className="flex items-stretch gap-2">
                <Input
                  type="text"
                  placeholder="Key name (e.g. PHAFL Portal)"
                  value={keyName}
                  onChange={(e) => setKeyName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") generate(); }}
                  maxLength={100}
                  disabled={generating || data?.disabled}
                  className="rounded-sm flex-1"
                  data-testid="sg-creds-key-name-input"
                />
                <Button type="button" size="sm" className="rounded-sm shrink-0"
                  onClick={generate}
                  disabled={generating || data?.disabled || !keyName.trim()}
                  data-testid="sg-creds-generate">
                  {generating ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <KeyRound className="h-3.5 w-3.5 mr-1" />}
                  Generate
                </Button>
              </div>
            </div>
          </div>
        ) : null}

        <DialogFooter>
          <Button variant="outline" className="rounded-sm" onClick={onClose}
            data-testid="sg-creds-close">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function StatsTab({ subusers }) {
  if (subusers.length === 0) {
    return (
      <Card className="rounded-sm border-dashed border-zinc-300">
        <CardContent className="p-8 text-center text-sm text-zinc-500">No subusers cached.</CardContent>
      </Card>
    );
  }
  return (
    <Card className="rounded-sm border-zinc-200 overflow-hidden">
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50/60 border-b border-zinc-100">
              <tr className="text-left">
                <th className="px-3 py-2 label-overline">Subuser</th>
                <th className="px-3 py-2 label-overline text-right">Requests</th>
                <th className="px-3 py-2 label-overline text-right">Delivered</th>
                <th className="px-3 py-2 label-overline text-right">Opens (u)</th>
                <th className="px-3 py-2 label-overline text-right">Clicks (u)</th>
                <th className="px-3 py-2 label-overline text-right">Bounces</th>
                <th className="px-3 py-2 label-overline text-right">Spam</th>
                <th className="px-3 py-2 label-overline text-right">Unsub</th>
              </tr>
            </thead>
            <tbody>
              {subusers.map((s) => {
                const m = s.stats || {};
                return (
                  <tr key={s.username} className="border-b border-zinc-50"
                    data-testid={`sg-stats-row-${s.username}`}>
                    <td className="px-3 py-2 font-mono text-[12px]">{s.username}</td>
                    <td className="px-3 py-2 text-right font-mono text-[12px]">{Number(m.requests || 0).toLocaleString()}</td>
                    <td className="px-3 py-2 text-right font-mono text-[12px]">{Number(m.delivered || 0).toLocaleString()}</td>
                    <td className="px-3 py-2 text-right font-mono text-[12px]">{Number(m.unique_opens || 0).toLocaleString()}</td>
                    <td className="px-3 py-2 text-right font-mono text-[12px]">{Number(m.unique_clicks || 0).toLocaleString()}</td>
                    <td className="px-3 py-2 text-right font-mono text-[12px]">{Number(m.bounces || 0).toLocaleString()}</td>
                    <td className="px-3 py-2 text-right font-mono text-[12px]">{Number(m.spam_reports || 0).toLocaleString()}</td>
                    <td className="px-3 py-2 text-right font-mono text-[12px]">{Number(m.unsubscribes || 0).toLocaleString()}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

function SuppressionsTab({ configured }) {
  const [kind, setKind] = useState("bounce");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!configured) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/sendgrid/suppressions?kind=${kind}&limit=500`);
      setRows(data.rows || []);
    } catch (e) {
      toast.error("Failed to load suppressions");
    } finally { setLoading(false); }
  }, [kind, configured]);
  useEffect(() => { load(); }, [load]);

  if (!configured) {
    return (
      <Card className="rounded-sm border-dashed border-zinc-300">
        <CardContent className="p-8 text-center text-sm text-zinc-500">Save the master API key first.</CardContent>
      </Card>
    );
  }
  return (
    <Card className="rounded-sm border-zinc-200">
      <CardContent className="p-5">
        <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
          <div className="label-overline flex items-center gap-1">
            <ShieldAlert className="h-3 w-3" /> Master-scope suppressions
          </div>
          <div className="flex items-center gap-2">
            <Select value={kind} onValueChange={setKind}>
              <SelectTrigger className="rounded-sm w-[160px]" data-testid="sg-supp-kind">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="bounce">Bounces</SelectItem>
                <SelectItem value="block">Blocks</SelectItem>
                <SelectItem value="spam_report">Spam reports</SelectItem>
                <SelectItem value="invalid">Invalid</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" className="rounded-sm" onClick={load}>
              <RefreshCw className="h-3.5 w-3.5 mr-1" /> Reload
            </Button>
          </div>
        </div>
        {loading ? (
          <div className="py-8 text-center text-sm text-zinc-500 flex items-center justify-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : rows.length === 0 ? (
          <div className="py-8 text-center text-sm text-zinc-400">No {kind} entries cached yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50/60 border-b border-zinc-100">
                <tr className="text-left">
                  <th className="px-3 py-2 label-overline">Email</th>
                  <th className="px-3 py-2 label-overline">Reason</th>
                  <th className="px-3 py-2 label-overline">When</th>
                </tr>
              </thead>
              <tbody>
                {rows.slice(0, 500).map((r) => (
                  <tr key={r.email + (r.created || "")} className="border-b border-zinc-50"
                    data-testid={`sg-supp-row-${r.email}`}>
                    <td className="px-3 py-2 font-mono text-[12px]">{r.email}</td>
                    <td className="px-3 py-2 text-[12px] text-zinc-700">{r.reason || r.status}</td>
                    <td className="px-3 py-2 font-mono text-[11px] text-zinc-500">
                      {r.created ? new Date(Number(r.created) * 1000).toLocaleString() : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
